import {
  CloseOutlined,
  PauseOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import { Progress } from "antd";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  AiOutlineClose as X,
  AiOutlineDownload as Download,
} from "react-icons/ai";
import { FaPlayCircle, FaSearchMinus, FaSearchPlus } from "react-icons/fa";
import { FaDownload } from "react-icons/fa6";
import { TfiLayoutWidthDefault } from "react-icons/tfi";
import { standardizeVolume } from "../../utils/Utils";
import { CustomAvatar } from "../avatar/CustomAvatar";
import ImageDisplay from "../chat/ImageDisplay";
import "./style.scss";

import { LuRotateCcw, LuRotateCw } from "react-icons/lu";
import { TbFlipHorizontal, TbFlipVertical } from "react-icons/tb";
import { useMenuContext } from "../../context/MenuContext";
import { useInfoUser } from "../../store/UserStore";
import { useGlobalDownloader } from "../../context/global/DownloadProvider";
import { useAuthLayoutContext } from "../../context/AuthLayoutContext";

export default function MediaViewerModal({ mediaResource }) {
  const { languageMap } = useInfoUser();
  const { orgList } = useAuthLayoutContext();

  const {
    isLoading,
    mediaResourceDates,
    loadMoreAsc,
    loadMoreDesc,
    selectedMedia,
    handleClose,
  } = mediaResource;
  const { fetchMediaResource } = mediaResource;

  const firstObserver = useRef();
  const lastObserver = useRef();
  const pendingScrollRestore = useRef(null);
  const hasInitialSelectedScroll = useRef(false);

  const activeRef = useRef(null);
  const scrollRef = useRef(null);
  const [scaleImg, setScaleImg] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imagePos, setImagePos] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [flipX, setFlipX] = useState(false);
  const [flipY, setFlipY] = useState(false);

  const imgContainerRef = useRef(null);
  const { selectedConversation } = useMenuContext();

  const firstElementRef = useCallback(
    (node) => {
      if (!node) {
        firstObserver.current?.disconnect();
        return;
      }
      if (!loadMoreAsc) return;

      firstObserver.current?.disconnect();
      firstObserver.current = new IntersectionObserver(
        async ([entry]) => {
          if (!entry.isIntersecting || isLoading || !loadMoreAsc) return;

          const container = scrollRef.current;
          const previousScrollHeight = container?.scrollHeight || 0;
          const previousScrollTop = container?.scrollTop || 0;

          pendingScrollRestore.current = {
            container,
            previousScrollHeight,
            previousScrollTop,
          };
          firstObserver.current?.disconnect();
          await fetchMediaResource("asc");
        },
        {
          root: scrollRef.current,
          rootMargin: "24px 0px",
          threshold: 0,
        },
      );
      firstObserver.current.observe(node);
    },
    [isLoading, loadMoreAsc, fetchMediaResource],
  );

  const lastElementRef = useCallback(
    (node) => {
      if (!node) {
        lastObserver.current?.disconnect();
        return;
      }
      if (!loadMoreDesc) return;

      lastObserver.current?.disconnect();
      lastObserver.current = new IntersectionObserver(
        async ([entry]) => {
          if (!entry.isIntersecting || isLoading || !loadMoreDesc) return;
          lastObserver.current?.disconnect();
          await fetchMediaResource("desc");
        },
        {
          root: scrollRef.current,
          rootMargin: "24px 0px",
          threshold: 0,
        },
      );
      lastObserver.current.observe(node);
    },
    [isLoading, loadMoreDesc, fetchMediaResource],
  );

  useEffect(() => {
    const pending = pendingScrollRestore.current;
    if (pending?.container) {
      const { container, previousScrollHeight, previousScrollTop } = pending;
      container.scrollTop =
        previousScrollTop + (container.scrollHeight - previousScrollHeight);
      pendingScrollRestore.current = null;
      return;
    }

    if (!hasInitialSelectedScroll.current && activeRef.current) {
      activeRef.current.scrollIntoView({
        behavior: "auto",
        block: "center",
      });
      hasInitialSelectedScroll.current = true;
    }
  }, [mediaResourceDates]);

  useEffect(() => {
    return () => {
      firstObserver.current?.disconnect();
      lastObserver.current?.disconnect();
    };
  }, []);

  const formatDateTime = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const {
    downloads,
    startDownload,
    cancelDownload,
    pauseDownload,
    resumeDownload,
    DOWNLOAD_STATUS,
  } = useGlobalDownloader();

  const [fileState, setFileState] = useState({});
  const fileNameToSave = selectedMedia?.originalName || "Willo-media";

  const handleResumeDownload = (e) => {
    e.stopPropagation();
    resumeDownload(selectedMedia?.mainPath, fileNameToSave);
  };

  const handlePauseDownload = (e) => {
    e.stopPropagation();
    pauseDownload(selectedMedia?.mainPath);
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    startDownload(selectedMedia?.mainPath, fileNameToSave, 0);
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    cancelDownload(selectedMedia?.mainPath);
  };

  const renderDownloadButton = useCallback(() => {
    switch (fileState?.status) {
      case DOWNLOAD_STATUS.PAUSED:
        return (
          <div className="flex gap-2">
            <button onClick={handleResumeDownload}>
              <PlayCircleOutlined
                style={{ fontSize: "20px", color: "green" }}
              />
            </button>
            <button onClick={handleCancel}>
              <CloseOutlined style={{ fontSize: "20px", color: "red" }} />
            </button>
          </div>
        );
      case DOWNLOAD_STATUS.DOWNLOADING:
        return (
          <button onClick={handlePauseDownload}>
            <PauseOutlined style={{ fontSize: "20px", color: "white" }} />
          </button>
        );

      case DOWNLOAD_STATUS.SUCCESS:
        return (
          <button onClick={handleDownload}>
            <FaDownload style={{ fontSize: "20px", color: "white" }} />
          </button>
        );
      case DOWNLOAD_STATUS.ERROR:
        return (
          <div className="flex gap-2">
            <button onClick={handleCancel}>
              <CloseOutlined style={{ fontSize: "20px", color: "red" }} />
            </button>
          </div>
        );
      default:
        return (
          <button onClick={handleDownload}>
            <Download style={{ fontSize: "25px", color: "white" }} />
          </button>
        );
    }
  }, [fileState?.status, mediaResource?.selectedMedia]);

  const handleMouseDown = (e) => {
    if (scaleImg > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - imagePos.x,
        y: e.clientY - imagePos.y,
      });

      e.preventDefault();
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setImagePos({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });

      e.preventDefault();
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleZoomIn = () => {
    if (!imgContainerRef) return;

    setScaleImg((prevScale) => {
      const newScale = Math.min(prevScale + 0.5, 10);

      if (newScale <= 1) {
        setImagePos({
          x: 0,
          y: 0,
        });
      }

      return newScale;
    });
  };

  const handleZoomOut = () => {
    if (!imgContainerRef) return;

    setScaleImg((prevScale) => {
      const newScale = Math.max(prevScale - 0.3, 0.1);

      if (newScale <= 1) {
        setImagePos({ x: 0, y: 0 });
      }

      return newScale;
    });
  };

  const handleCenterLayout = () => {
    setImagePos({ x: 0, y: 0 });
    setScaleImg(1);
  };

  const handleWheel = (e) => {
    if (!imgContainerRef) return;

    setScaleImg((prevScale) => {
      const zoomIntensity = 0.1;
      const newScale =
        e.deltaY < 0
          ? prevScale + zoomIntensity
          : Math.max(prevScale - zoomIntensity, 0.1);

      if (newScale <= 1) {
        setImagePos({ x: 0, y: 0 });
      }
      return Math.min(newScale, 10);
    });
  };

  const handleRotateLeft = () => setRotation((prev) => prev - 90);
  const handleRotateRight = () => setRotation((prev) => prev + 90);

  const handleFlipX = () => setFlipX((prev) => !prev);
  const handleFlipY = () => setFlipY((prev) => !prev);

  const getSender = () => {
    const sender = orgList?.find(
      (org) => org?.userCode === selectedMedia?.senderId,
    );
    return {
      ...sender,
      name: sender?.name ? sender?.name : selectedMedia?.senderName,
      userId: sender?.userCode ? sender?.userCode : selectedMedia?.senderId,
      position: sender?.userPosition,
    };
  };

  useEffect(() => {
    setScaleImg(1);
    setRotation(0);
    setFlipX(false);
    setFlipY(false);
    hasInitialSelectedScroll.current = false;
    if (activeRef.current) {
      activeRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      hasInitialSelectedScroll.current = true;
    }
  }, [selectedMedia]);

  useEffect(() => {
    setFileState(downloads[selectedMedia?.mainPath]);
  }, [downloads, selectedMedia?.mainPath]);

  if (!selectedMedia) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center p-3 bg-neutral-900 text-white text-sm">
        <span className="font-medium">
          {languageMap[selectedConversation?.name] ||
            selectedConversation?.name}
        </span>
        <button
          onClick={handleClose}
          className="p-1 hover:bg-neutral-700 rounded transition"
        >
          <X size={20} />
        </button>
      </div>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Main viewer */}
        <div
          className="flex-1 flex justify-center items-center bg-black overflow-hidden"
          onClick={handleClose}
        >
          {selectedMedia?.contentType === "VIDEO" ? (
            <div
              className="display-imgage h-[100%]"
              onClick={(e) => e.stopPropagation()}
            >
              <video
                src={selectedMedia?.mainPath}
                controls
                autoPlay
                width="100%"
                controlsList="nodownload"
                className="img-preview h-[100%] pb-[30px]"
              >
                The browser does not support the video tag.
              </video>
            </div>
          ) : (
            <button
              ref={imgContainerRef}
              className="display-imgage h-[100%]"
              onClick={(e) => e.stopPropagation()}
              onWheel={handleWheel}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              <div
                className="h-[100%] flex justify-center items-center relative"
                style={{
                  transform: `
                    translate(${imagePos.x}px, ${imagePos.y}px)
                    scale(${scaleImg})
                    rotate(${rotation}deg)
                    scaleX(${flipX ? -1 : 1})
                    scaleY(${flipY ? -1 : 1})
                  `,
                  transformOrigin: "center center",
                  transition: "transform 0.15s ease-out",
                  willChange: "transform",
                }}
              >
                <ImageDisplay
                  path={selectedMedia?.path || selectedMedia?.mainPath}
                  minioPath={selectedMedia?.mainPath}
                  imageStyle={{
                    maxHeight: "100%",
                    width: "auto",
                    objectFit: "cover",
                    display: "block",
                    background: "white",
                  }}
                  classNameCss="img-preview"
                  showLoading={false}
                />
              </div>
            </button>
          )}

          <div
            className="absolute bottom-[65px] mr-[130px] left-0 right-0 flex justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-gray-300 text-sm bg-black/60 px-3 py-1 rounded-md max-w-[80%] truncate">
              {selectedMedia?.originalName ||
                selectedMedia?.fileName ||
                selectedMedia?.mainPath}
            </p>
          </div>
        </div>
        {/*Sidebar thumbnails grouped by date */}
        <div className="w-[130px] z-10 bg-neutral-950 border-l border-neutral-800 flex flex-col justify-between">
          <div
            ref={scrollRef}
            className="flex flex-col items-center gap-4 overflow-y-auto py-3 scrollbar-custom"
          >
            {mediaResourceDates?.map((mediaResourceDate, index) => {
              if (index === 0) {
                //add more first shadow
                return (
                  <React.Fragment key={mediaResourceDate?.key}>
                    <div
                      key={"first-shadow"}
                      ref={firstElementRef}
                      className="h-px w-full"
                      aria-hidden="true"
                    />
                    <div className="text-[white]">{mediaResourceDate?.key}</div>

                    {mediaResourceDate?.files?.map((file) => {
                      const isActive =
                        file?.mainPath === selectedMedia?.mainPath;

                      const dateLabel = file?.date;

                      return (
                        <button
                          key={file?.mainPath}
                          ref={isActive ? activeRef : null}
                          type="button"
                          className={`rounded relative ${
                            isActive
                              ? "ring-2 ring-[#275efe] p-[1px]"
                              : "opacity-70"
                          }`}
                          onClick={() => {
                            mediaResource?.setSelectedMedia(file)
                          }}
                        >
                          <ImageDisplay
                            path={file?.thumbnailPreviewPath || file?.mainPath}
                            minioPath={
                              file?.videoImgPreviewPath ||
                              file?.thumbnailPreviewPath ||
                              file?.mainPath
                            }
                            classNameCss="w-[90px] rounded m-[1px] h-[80px] object-cover bg-white"
                          />
                          {file?.videoImgPreviewPath && (
                            <span className="media-thumbnail-play">
                              <FaPlayCircle size={25} />
                            </span>
                          )}
                          <span className="media-thumbnail-date">
                            {dateLabel}
                          </span>
                        </button>
                      );
                    })}

                    {mediaResourceDates.length === 1 && (
                      <div
                        key="last-shadow-single-group"
                        ref={lastElementRef}
                        className="h-px w-full"
                        aria-hidden="true"
                      />
                    )}
                  </React.Fragment>
                );
              }

              if (index === mediaResourceDates?.length - 1) {
                return (
                  <React.Fragment key={mediaResourceDate?.key}>
                    <div
                      key={`${mediaResourceDate?.key}-label`}
                      className="text-[white]"
                    >
                      {mediaResourceDate?.key}
                    </div>

                    {mediaResourceDate?.files?.map((file) => {
                      const isActive =
                        file?.mainPath === selectedMedia?.mainPath;
                      const dateLabel = file?.date;

                      return (
                        <button
                          key={file?.mainPath}
                          ref={isActive ? activeRef : null}
                          type="button"
                          className={`rounded relative ${
                            isActive
                              ? "ring-2 ring-[#275efe] p-[1px]"
                              : "opacity-70"
                          }`}
                          onClick={() => {
                            mediaResource?.setSelectedMedia(file)
                          }}
                        >
                          <ImageDisplay
                            path={file?.thumbnailPreviewPath || file?.mainPath}
                            minioPath={
                              file?.videoImgPreviewPath ||
                              file?.thumbnailPreviewPath ||
                              file?.mainPath
                            }
                            classNameCss="w-[90px] rounded m-[1px] h-[80px] object-cover bg-white"
                          />

                          {file?.videoImgPreviewPath && (
                            <span className="media-thumbnail-play">
                              <FaPlayCircle size={25} />
                            </span>
                          )}

                          <span className="media-thumbnail-date">
                            {dateLabel}
                          </span>
                        </button>
                      );
                    })}

                    <div
                      key={"last-shadow"}
                      ref={lastElementRef}
                      className="h-px w-full"
                      aria-hidden="true"
                    />
                  </React.Fragment>
                );
              }

              return (
                <React.Fragment key={mediaResourceDate?.key}>
                  <div className="text-[white]">{mediaResourceDate?.key}</div>
                  {mediaResourceDate?.files?.map((file) => {
                    const isActive =
                      file?.mainPath === selectedMedia?.mainPath;
                    const dateLabel = file?.date;

                    return (
                      <button
                        ref={isActive ? activeRef : null}
                        key={file?.mainPath}
                        type="button"
                        className={`rounded relative ${
                          isActive
                            ? "ring-2 ring-[#275efe] p-[1px]"
                            : "opacity-70"
                        }`}
                        onClick={() => {
                          mediaResource?.setSelectedMedia(file)
                        }}
                      >
                        <ImageDisplay
                          path={file?.thumbnailPreviewPath || file?.mainPath}
                          minioPath={
                            file?.videoImgPreviewPath ||
                            file?.thumbnailPreviewPath ||
                            file?.mainPath
                          }
                          classNameCss="w-[90px] rounded m-[1px] h-[80px] object-cover bg-white"
                        />
                        {file?.videoImgPreviewPath && (
                          <span className="media-thumbnail-play">
                            <FaPlayCircle size={25} />
                          </span>
                        )}
                        <span className="media-thumbnail-date">
                          {dateLabel}
                        </span>
                      </button>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex pr-[130px] items-center justify-between p-3 bg-neutral-900 text-gray-300 text-sm">
        <div className="flex items-center gap-2 min-w-[250px]">
          <CustomAvatar
            person={getSender()}
            userId={getSender()?.userId}
            styleCss={{}}
          />
          <div>
            <p className="font-medium leading-tight">{getSender()?.name}</p>
            <p className="text-xs">
              {formatDateTime(selectedMedia?.createdAt)}
            </p>
          </div>
        </div>

        <div className="action-image flex items-center gap-2">
          <div className="flex justify-center gap-[10px]">
            <div className="icon-detail cursor-pointer flex gap-5 ">
              {!selectedMedia?.videoImgPreviewPath && (
                <>
                  <button
                    className="icon-detail p-1 hover:text-white transition"
                    onClick={handleZoomOut}
                  >
                    <FaSearchMinus size={20} />
                  </button>
                  <button
                    className="icon-detail p-1 hover:text-white transition"
                    onClick={handleCenterLayout}
                  >
                    <TfiLayoutWidthDefault size={20} />
                  </button>
                  <button
                    className="icon-detail p-1 hover:text-white transition"
                    onClick={handleZoomIn}
                  >
                    <FaSearchPlus size={20} />
                  </button>

                  <button
                    className="p-1 hover:text-white transition"
                    onClick={handleRotateLeft}
                  >
                    <LuRotateCcw size={20} />
                  </button>
                  <button
                    className="p-1 hover:text-white transition"
                    onClick={handleRotateRight}
                  >
                    <LuRotateCw size={20} />
                  </button>

                  <button
                    className="p-1 hover:text-white transition"
                    onClick={handleFlipX}
                  >
                    <TbFlipHorizontal size={20} />
                  </button>
                  <button
                    className="p-1 hover:text-white transition"
                    onClick={handleFlipY}
                  >
                    <TbFlipVertical size={20} />
                  </button>
                </>
              )}
              <div className="relative flex items-center">
                {renderDownloadButton()}

                {[DOWNLOAD_STATUS.DOWNLOADING].includes(fileState?.status) && (
                  <div
                    className="absolute left-full gap-2 ml-2 top-1/2 -translate-y-1/2 
                      flex items-center bg-black/40 px-2 py-[2px] rounded-md"
                  >
                    <div className="flex items-center">
                      <Progress
                        className="w-28 flex items-center"
                        percent={fileState?.progress}
                        showInfo={false}
                        strokeColor="#52c41a"
                        size="small"
                      />
                      <div className="text-[grey] text-[10px] ml-2 whitespace-nowrap">
                        {standardizeVolume(
                          (fileState?.progress * selectedMedia?.volume) / 100,
                        )}{" "}
                        / {standardizeVolume(selectedMedia?.volume)}
                      </div>
                    </div>

                    {[DOWNLOAD_STATUS.DOWNLOADING].includes(
                      fileState?.status,
                    ) && (
                      <button
                        className="flex items-center"
                        onClick={handleCancel}
                      >
                        <CloseOutlined
                          style={{ fontSize: "20px", color: "white" }}
                        />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 w-[250px]"></div>
      </div>
    </div>
  );
}
